import * as THREE from 'three';
import getChromaKeyMaterial from './chromakey/ChromaKeyMaterial';
import { loadImageAsync } from '../utils/AssetLoader';

const PINK_PLAZA_SCENE = '5f6cc3bdef2358bc6a71796f'; // pink-plaza-5
const TIMEWISE_SCENE = '5f5948292ea4e05c69283ced' // timewise-repair
const CLEARPROOF_SCENE = '5f5947871f2c164d4c0ac1ed' // clearproof
const BOTANICAL_SCENE = '5f5add351d0ccdee898fe65c' // botanical-effects-3
const COLOR_STUDIO_1 = '62321a178cebd8804828e095'; //color-studio-1
const COLOR_STUDIO_2 = '62321a188cebd8804828e097'; //color-studio-2
const COLOR_STUDIO_3 = '62321a198cebd8804828e099'; //color-studio-3
const COLOR_STUDIO_4 = '62604106647fa0fd0e171284'; //color-studio-4
const COLOR_STUDIO_5 = '62604134631385ee671a4a61'; //color-studio-5

export default class VideoPlane {
    constructor(width, height, url, onVideoEnd, imageUrl=null, chromaColor=null, roomId=null, addToRenderLoop = null, removeFromRenderLoop = null, videoReadyState) {
        if (typeof (width) !== 'number' || typeof (height) !== 'number') {
            throw new Error('width and height must be numbers!');
        }
        this.addToScene = this.addToScene.bind(this);
        this.removeFromScene = this.removeFromScene.bind(this);
        this.setTransform = this.setTransform.bind(this);
        this.setPosition = this.setPosition.bind(this);
        this.setRotation = this.setRotation.bind(this);
        this.play = this.play.bind(this);
        this.pause = this.pause.bind(this);
        this.end = this.end.bind(this);

		this.videoLoadedState = this.videoLoadedState.bind(this);
        this.isPlaying = this.isPlaying.bind(this);
        this.isVisible = this.isVisible.bind(this);

        this.imageUrl = imageUrl;
        this.image = null;
        if (imageUrl) {
            const imageGeometry = new THREE.PlaneGeometry(1, 1);
            const imageMaterial = new THREE.MeshBasicMaterial();
            this.image = new THREE.Mesh(imageGeometry, imageMaterial);
            this.image.visible = false;
            loadImageAsync(
                imageUrl,
                (image) => {
                    this.image.material.map = image;
                    this.image.material.needsUpdate = true;
                    this.image.material.transparent = true;
                    this.image.visible = true;
                },
                null,
                (error) => {
                    console.log("error: " + error + ", loading image with url: " + url);
                },
                roomId
            );
        }

		this.videoReadyState = videoReadyState;
        this.roomId = roomId;
        this.onVideoEnd = onVideoEnd;
        this.video = document.createElement('video');
        this.video.src = url;
        this.video.crossOrigin = 'anonymous';
        this.video.setAttribute('webkit-playsinline', '');
        this.video.setAttribute('playsinline', '');
        this.video.currentTime = 1;
		this.video.playsInline = true;
        this.video.muted = true;0
        this.video.load();
		if (this.videoReadyState) {
            this.video.currentTime = 0;
        } else {
            this.video.currentTime = 1;
        }
        this.video.addEventListener('ended', this.end, false);
		if (this.videoReadyState) {
            this.video.addEventListener('loadeddata', this.videoLoadedState, false)
        }

        const texture = new THREE.VideoTexture(this.video);
        texture.minFilter = THREE.LinearFilter;
        texture.magFilter = THREE.LinearFilter;
        texture.format = THREE.RGBFormat;

        const geometry = new THREE.PlaneGeometry(width, height);
        const material = chromaColor === null ? new THREE.MeshBasicMaterial({ side: THREE.FrontSide }) : getChromaKeyMaterial(texture, chromaColor);
        material.map = texture;
        material.needsUpdate = true;
        this.plane = new THREE.Mesh(geometry, material);
        this.plane.visible = false;
		this.plane.userData.isVideoPlane = true
        this.addToRenderLoop = addToRenderLoop;
		this.removeFromRenderLoop = removeFromRenderLoop;
		this.renderUUID = null;
    }

	videoLoadedState() {
        if (this.video && this.video.readyState === 4 && this.videoReadyState) {
            this.plane.visible = true;
            this.plane.userData.shouldBeVisible = true
            this.videoReadyState(true)
        }
    } 

    addToScene(scene) {
        scene.add(this.plane);
		this.renderUUID = this.addToRenderLoop();
        if (this.imageUrl) {
            scene.add(this.image);
        }
    }

    removeFromScene(scene) {
		scene.remove(this.plane);
		if (this.renderUUID) {
			this.removeFromRenderLoop(this.renderUUID);
		}
		if (this.imageUrl) {
			scene.remove(this.image);
			this.image = null;
		}
		this.plane = null;
		this.pause();
		this.video = null;
    }

    setTransform(transform, imageTransform = null) {
        this.plane.matrix = transform;
        this.plane.matrix.decompose(this.plane.position, this.plane.quaternion, this.plane.scale);
    
        if (this.imageUrl) {
            this.image.matrix = imageTransform;
            this.image.matrix.decompose(this.image.position, this.image.quaternion, this.image.scale);
        }
        console.log("!#", this.roomId);
    
        // if (this.videoReadyState) {
			if (this.roomId === PINK_PLAZA_SCENE) {
				this.setPosition(-5, 0.1, -0.042);
				this.setScale(0.0043, 0.0042, 1);
				this.plane.rotateOnAxis(new THREE.Vector3(0, 1, 0), THREE.Math.degToRad(-270));
			} else if (this.roomId === TIMEWISE_SCENE) {
				this.setPosition(-3.3, 0.93, 5);
				this.setScale(0.0071, 0.00555, 1);
				this.plane.rotateOnAxis(new THREE.Vector3(0, 1, 0), THREE.Math.degToRad(-180));
			} else if (this.roomId === CLEARPROOF_SCENE) {
				this.setPosition(-3.67, 1.05, -5);
				this.setScale(0.00797, 0.00623, 1);
				this.plane.rotateOnAxis(new THREE.Vector3(0, 1, 0), THREE.Math.degToRad(0));
			} else if (this.roomId === BOTANICAL_SCENE) {
				this.setPosition(-5, 1.25, -0.05);
				this.setScale(0.0041, 0.00442, 1);
				this.plane.rotateOnAxis(new THREE.Vector3(0, 1, 0), THREE.Math.degToRad(-270));
			} else if (this.roomId === COLOR_STUDIO_1 && this.video.src.includes('99165Sizzle-Product-Promo.mp4')) {
                console.log(this.video.src);
                this.setPosition(-9.3, -0.250, -1.890);
                this.setRotation(-2.290, 1.495, 2.290);
                this.setScale(-0.012, 0.029, 0.705);
                this.plane.visible = true;
                this.playActions();
            } else if (this.roomId === COLOR_STUDIO_2) {
                console.log(this.video.src);
                this.setPosition(3.05, -0.4925, 9);
                this.setRotation(0.11, 3.15, 0.025);
                this.setScale(0.023, 0.0545, 0.705);
                this.plane.visible = true;
                this.playActions();
            } else if (this.roomId === COLOR_STUDIO_3) {
                console.log(this.video.src);
                this.setPosition(8.170, 0.520, -7.350);
                this.setRotation(-0.043, -0.052, -0.030);
                this.setScale(-0.018, 0.046, 0.585);
                this.plane.visible = true;
                this.playActions();
            }
			 else if (this.roomId === COLOR_STUDIO_4 && this.video.src.includes('44409sizzelMK_compressed.mp4')) {
				console.log(this.video.src);
				this.setPosition(9.800, -1.790, 3.730);
				this.setRotation(-0.051, -1.723, -0.039);
				this.setScale(0.002, 0.002, 0.175);
				this.plane.visible = true;
				this.playActions();
			} 
			 else if (this.roomId === COLOR_STUDIO_5 && this.video.src.includes('90482sizzelMK_compressed.mp4')) {
				console.log(this.video.src);
				this.setPosition(7.570, 0.320, -6.930);
				this.setRotation(3.131, -3.153, 3.143);
				this.setScale(0.001, 0.003, 0.175);
				this.plane.visible = true;
				this.playActions();
			} 
		// }
    }

    playActions() {
        this.video.play()
        this.plane.visible = true
        this.plane.userData.shouldBeVisible = true
        if (this.imageUrl) {
            this.image.visible = false
        }
    }

    setPosition(x, y, z) {
        this.plane.position.set(x, y, z);
    }

    setRotation(x, y, z) {
        this.plane.rotation.x = x;
        this.plane.rotation.y = y;
        this.plane.rotation.z = z;
    }

    setScale(x, y, z) {
        this.plane.scale.x = x;
        this.plane.scale.y = y;
        this.plane.scale.z = z;
    }

    play() {
        if (this?.video?.readyState === 4) {
            this.playActions();
        } else {
            this.video.addEventListener("loadeddata", () => {
                this.playActions();
            })
        }

    }

    pause() {
        this.video.pause();
    }

    end() {
        this.plane.visible = false;
        if (this.imageUrl) {
            this.image.visible = true;
        }
        if (this.onVideoEnd) {
            this.onVideoEnd();
        }
    }

    isPlaying() {
        return !this.video.paused;
    }

    isVisible() {
        return this.plane.visible;
    }
}